/**
 * SUPPLIERS — WHO WE BUY FROM.
 *
 * Small on purpose. What a person needs here is the name, what they supply,
 * and how many orders are still open with them — that last figure is why the
 * screen exists at all, because closing a supplier who still owes you forty
 * cylinders of argon is the mistake it prevents (the command refuses it, and
 * the count above says so before anybody tries).
 */
import * as React from 'react';
import { useState } from 'react';
import { api, can, type Session } from '../api.js';
import { useAsk, useRun, go } from '../hooks.js';
import { Banner, Button, Card, Check, Loading, Pill, Register, Text, TextArea, type Column } from '../ui.js';
import { Pager } from '../pager.js';
import { Page } from '../shell.js';
import { count, today } from '../format.js';
import { screenById } from '../../../src/screens/contracts.js';

const MESSAGES = screenById('suppliers')!.messages;

export function Suppliers({ session }: { session: Session }) {
  const [q, setQ] = useState(''); const [onlyActive, setOnlyActive] = useState(true);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const asked = useAsk(() => api.view('suppliers.list', {
    ...(q.trim() ? { q: q.trim() } : {}), ...(onlyActive ? { active: true } : {}), page, pageSize: 50,
  }), [q, onlyActive, page], MESSAGES);
  const may = can(session, 'supplier.manage');

  const columns: readonly Column<{ supplierId: number; name: string; code: string | null; supplies: string | null; active: boolean; openOrders: number }>[] = [
    { key: 'name', head: 'Supplier', cell: (r) => <><strong>{r.name}</strong>{r.code ? <div className="muted">{r.code}</div> : null}</> },
    { key: 'supplies', head: 'What they supply', cell: (r) => r.supplies ?? <span className="muted">—</span> },
    { key: 'open', head: 'Open orders', right: true, cell: (r) => r.openOrders > 0
        ? <a href={`#/purchases?supplier=${r.supplierId}`}><strong>{count(r.openOrders)}</strong></a>
        : <span className="muted">none</span> },
    { key: 'state', head: 'Status', cell: (r) => <Pill tone={r.active ? 'good' : 'muted'}>{r.active ? 'open' : 'closed'}</Pill> },
    { key: 'edit', head: '', cell: (r) => may && r.active
        ? <Button small onClick={() => setEditing((v) => v === r.supplierId ? null : r.supplierId)}>{editing === r.supplierId ? 'Close' : 'Correct'}</Button> : null },
  ];

  return (
    <Page title="Suppliers" sub="Who we buy from, and what is still open with each of them."
          actions={<>
            <Button onClick={() => go('#/purchases')}>Purchase orders</Button>
            {may ? <Button kind="primary" onClick={() => setAdding((v) => !v)}>{adding ? 'Cancel' : 'Add a supplier'}</Button> : null}
          </>}>
      <div className="toolbar">
        <div className="field grow">
          <label htmlFor="q">Search</label>
          <input id="q" type="text" value={q} placeholder="Name or code" onChange={(e: { target: { value: string } }) => setQ(e.target.value)} />
        </div>
        <Check name="only-open" checked={onlyActive} onChange={setOnlyActive} label="Only the ones we still buy from" />
      </div>

      {adding ? <SupplierForm onDone={() => { setAdding(false); asked.reload(); }} /> : null}

      <Card>
        {asked.loading ? <Loading what="suppliers" /> : asked.error ? <Banner kind="error" title={asked.error.title} {...(asked.error.detail ? { detail: asked.error.detail } : {})} />
          : (<>
              <Register columns={columns} rows={asked.data!.rows} empty="No supplier matches that."
 />
              <Pager page={asked.data!.page} onPage={setPage} />
            </>)}
      </Card>

      {editing !== null && asked.data ? (
        <SupplierForm supplier={asked.data.rows.find((r) => r.supplierId === editing)!} onDone={() => { setEditing(null); asked.reload(); }} />
      ) : null}
    </Page>
  );
}

function SupplierForm({ supplier, onDone }: {
  supplier?: { supplierId: number; name: string; code: string | null; supplies: string | null; openOrders: number };
  onDone: () => void;
}) {
  const [name, setName] = useState(supplier?.name ?? '');
  const [code, setCode] = useState(supplier?.code ?? '');
  const [supplies, setSupplies] = useState(supplier?.supplies ?? '');
  const [closing, setClosing] = useState(false);
  const [closedOn, setClosedOn] = useState(today());
  const [reason, setReason] = useState('');
  const runner = useRun(MESSAGES);

  async function save() {
    const ok = await runner.run(() => supplier
      ? api.command('supplier.update', {
          supplierId: supplier.supplierId,
          ...(name.trim() !== supplier.name ? { name: name.trim() } : {}),
          ...(code.trim() !== (supplier.code ?? '') ? { code: code.trim() } : {}),
          ...(supplies.trim() !== (supplier.supplies ?? '') ? { supplies: supplies.trim() } : {}),
        })
      : api.command('supplier.create', {
          name: name.trim(),
          ...(code.trim() ? { code: code.trim() } : {}),
          ...(supplies.trim() ? { supplies: supplies.trim() } : {}),
        }));
    if (ok) onDone();
  }
  async function close() {
    if (!supplier) return;
    const ok = await runner.run(() => api.command('supplier.close', { supplierId: supplier.supplierId, closedOn, reason: reason.trim() }));
    if (ok) onDone();
  }

  return (
    <Card title={supplier ? `Correct ${supplier.name}` : 'Add a supplier'}>
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      <div className="grid two">
        <div><Text name="sup-name" label="Name" value={name} onChange={setName} required autoFocus />
             <Text name="sup-code" label="Code" value={code} onChange={setCode} /></div>
        <div><TextArea name="sup-supplies" label="What they supply" value={supplies} onChange={setSupplies} /></div>
      </div>
      <div className="toolbar">
        <Button kind="primary" onClick={() => { void save(); }} disabled={runner.busy || !name.trim()}>
          {runner.busy ? 'Saving…' : supplier ? 'Save the correction' : 'Add them'}
        </Button>
        {supplier ? <Button kind="danger" onClick={() => setClosing((v) => !v)}>{closing ? 'Keep them open' : 'Close this supplier'}</Button> : null}
      </div>
      {closing && supplier ? (
        <>
          {supplier.openOrders > 0 ? (
            <Banner kind="warn" title={`${count(supplier.openOrders)} purchase order(s) are still open with them.`}
                    detail="The command refuses to close a supplier with open orders, and it is right to — close or cancel those first." />
          ) : null}
          <Text name="sup-closed-on" label="Closed on" type="date" value={closedOn} onChange={setClosedOn} required />
          <TextArea name="sup-close-reason" label="Why" value={reason} onChange={setReason} />
          <Button kind="danger" onClick={() => { void close(); }} disabled={runner.busy || !reason.trim()}>
            {runner.busy ? 'Closing…' : 'Close them'}
          </Button>
        </>
      ) : null}
    </Card>
  );
}
